'use client';

import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './TermsCondition.scss';
import { useMainContext } from 'store/MainContext';

interface ArticleState {
	[key: string]: boolean;
}

export default function TermsAndConditions() {
	const { t, i18n } = useTranslation();
	const { mainState } = useMainContext();
	const isMerchant = useMemo(() => mainState.isMerchant, [mainState.isMerchant]);
	const termsUiLang = i18n.language === 'ar' ? 'ar' : 'en';
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [activePage, setActivePage] = useState('general');
	const [expandedArticles, setExpandedArticles] = useState<ArticleState>({
		'general-1': true,
		'protection-1': true,
		'disputes-1': true,
		'thirdparty-1': true,
		'client-terms-1': true,
		'client-ops-1': true,
		'violations-1': true,
		'merchant-terms-1': true,
		'merchant-ops-1': true,
		'merchant-violations-1': true,
	});
	const [showBackToTop, setShowBackToTop] = useState(false);

	useEffect(() => {
		const clientOnly = ['client-terms', 'client-ops', 'client-violations'];
		const merchantOnly = ['merchant-terms', 'merchant-ops', 'merchant-violations'];
		if (isMerchant && clientOnly.includes(activePage)) {
			setActivePage('merchant-terms');
		} else if (!isMerchant && merchantOnly.includes(activePage)) {
			setActivePage('client-terms');
		}
	}, [isMerchant, activePage]);

	useEffect(() => {
		const handleScroll = () => {
			setShowBackToTop(window.scrollY > 300);
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleArticle = (articleId: string) => {
		setExpandedArticles((prev) => ({
			...prev,
			[articleId]: !prev[articleId],
		}));
	};

	const handleNavigation = (pageId: string) => {
		setActivePage(pageId);
		setSidebarOpen(false);
	};

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const Text = ({ phraseKey }: { phraseKey: string }) => <>{t(phraseKey)}</>;

	return (
		<div className={`terms-container lang-${termsUiLang}`}>
			<button
				type='button'
				aria-label={t('termsTc_aria_toggleMenu') as string}
				className={`menu-toggle terms-mobile-menu-toggle ${sidebarOpen ? 'open' : ''}`}
				onClick={() => setSidebarOpen(!sidebarOpen)}
			>
				<span></span>
				<span></span>
				<span></span>
			</button>

			{/* Sidebar Overlay */}
			<div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

			<div className='layout'>
				{/* Sidebar */}
				<nav
					aria-label={t('termsTc_aria_policyNavigation') as string}
					className={`sidebar lang-${termsUiLang} ${sidebarOpen ? 'open' : ''}`}
					role='navigation'
				>
					{/* General Policy */}
					<div className='sidebar-group'>
						<div className='sidebar-group-title'>
							<Text phraseKey='termsTc_txt_General_Policy' />
						</div>
						<div
							className={`sidebar-item ${activePage === 'general' ? 'active' : ''}`}
							onClick={() => handleNavigation('general')}
						>
							<Text phraseKey='termsTc_txt_P2P_Platform_Terms_Conditions' />
						</div>
					</div>

					{/* Shared Policies */}
					<div className='sidebar-group'>
						<div className='sidebar-group-title'>
							<Text phraseKey='termsTc_txt_Shared_Policies' />
						</div>
						<div
							className={`sidebar-item ${activePage === 'protection' ? 'active' : ''}`}
							onClick={() => handleNavigation('protection')}
						>
							<Text phraseKey='termsTc_txt_Order_Protection_Balance_Escrow_P' />
						</div>
						<div
							className={`sidebar-item ${activePage === 'disputes' ? 'active' : ''}`}
							onClick={() => handleNavigation('disputes')}
						>
							<Text phraseKey='termsTc_txt_Disputes_Appeals_Policy' />
						</div>
						<div
							className={`sidebar-item ${activePage === 'thirdparty' ? 'active' : ''}`}
							onClick={() => handleNavigation('thirdparty')}
						>
							<Text phraseKey='termsTc_txt_Third_Party_Transactions_Policy' />
						</div>
					</div>

					{/* Clients — clients only */}
					{!isMerchant && (
						<div className='sidebar-group'>
							<div className='sidebar-group-title'>
								<Text phraseKey='termsTc_txt_Clients' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'client-terms' ? 'active' : ''}`}
								onClick={() => handleNavigation('client-terms')}
							>
								<Text phraseKey='termsTc_txt_Client_Terms_Conditions' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'client-ops' ? 'active' : ''}`}
								onClick={() => handleNavigation('client-ops')}
							>
								<Text phraseKey='termsTc_txt_Deposit_Withdrawal_Policy' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'client-violations' ? 'active' : ''}`}
								onClick={() => handleNavigation('client-violations')}
							>
								<Text phraseKey='termsTc_txt_Client_Violations_Penalties' />
							</div>
						</div>
					)}

					{/* Merchants — merchants only */}
					{isMerchant && (
						<div className='sidebar-group'>
							<div className='sidebar-group-title'>
								<Text phraseKey='termsTc_mt_nav_merchants' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'merchant-terms' ? 'active' : ''}`}
								onClick={() => handleNavigation('merchant-terms')}
							>
								<Text phraseKey='termsTc_mt_nav_merchant_terms' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'merchant-ops' ? 'active' : ''}`}
								onClick={() => handleNavigation('merchant-ops')}
							>
								<Text phraseKey='termsTc_mt_nav_merchant_ops' />
							</div>
							<div
								className={`sidebar-item ${activePage === 'merchant-violations' ? 'active' : ''}`}
								onClick={() => handleNavigation('merchant-violations')}
							>
								<Text phraseKey='termsTc_mt_nav_merchant_violations' />
							</div>
						</div>
					)}
				</nav>

				{/* Main Content */}
				<main className={`main-content lang-${termsUiLang}`} role='main'>
					{/* General Page */}
					{activePage === 'general' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-general'>
									<Text phraseKey='termsTc_txt_General_Policy_1' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_P2P_Direct_Payment_Platform_Terms' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_The_general_regulatory_framework_go' />
								</p>
							</div>

							<div className='intro-block'>
								<p>{t('termsTc_intro_general_p1')}</p>
								<p>{t('termsTc_intro_general_p2')}</p>
								<p>{t('termsTc_intro_general_p3')}</p>
							</div>

							{/* Article 1 - Definitions */}
							<div className={`article-block ${expandedArticles['general-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_Definitions' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='1.1'
										body={
											<>
												<strong>{t('termsTc_c_1_1_lead')}</strong>
												{t('termsTc_c_1_1_tail')}
											</>
										}
									/>
									<Clause
										num='1.2'
										body={
											<>
												<strong>{t('termsTc_c_1_2_lead')}</strong>
												{t('termsTc_c_1_2_tail')}
											</>
										}
									/>
									<Clause
										num='1.3'
										body={
											<>
												<strong>{t('termsTc_c_1_3_lead')}</strong>
												{t('termsTc_c_1_3_tail')}
											</>
										}
									/>
									<Clause
										num='1.4'
										body={
											<>
												<strong>{t('termsTc_c_1_4_lead')}</strong>
												{t('termsTc_c_1_4_tail')}
											</>
										}
									/>
									<Clause
										num='1.5'
										body={
											<>
												<strong>{t('termsTc_c_1_5_lead')}</strong>
												{t('termsTc_c_1_5_tail')}
											</>
										}
									/>
									<Clause
										num='1.6'
										body={
											<>
												<strong>{t('termsTc_c_1_6_lead')}</strong>
												{t('termsTc_c_1_6_tail')}
											</>
										}
									/>

									<Clause
										num='1.7'
										body={
											<>
												<strong>{t('termsTc_c_1_7_lead')}</strong>
												{t('termsTc_c_1_7_tail')}
											</>
										}
									/>

									<Clause
										num='1.8'
										body={
											<>
												<strong>{t('termsTc_c_1_8_lead')}</strong>
												{t('termsTc_c_1_8_tail')}
											</>
										}
									/>

									<Clause
										num='1.9'
										body={
											<>
												<strong>{t('termsTc_c_1_9_lead')}</strong>
												{t('termsTc_c_1_9_tail')}
											</>
										}
									/>

									<Clause
										num='1.10'
										body={
											<>
												<strong>{t('termsTc_c_1_10_lead')}</strong>
												{t('termsTc_c_1_10_tail')}
											</>
										}
									/>

									<Clause
										num='1.11'
										body={
											<>
												<strong>{t('termsTc_c_1_11_lead')}</strong>
												{t('termsTc_c_1_11_tail')}
											</>
										}
									/>

									<Clause
										num='1.12'
										body={
											<>
												<strong>{t('termsTc_c_1_12_lead')}</strong>
												{t('termsTc_c_1_12_tail')}
											</>
										}
									/>

									<Clause
										num='1.13'
										body={
											<>
												<strong>{t('termsTc_c_1_13_lead')}</strong>
												{t('termsTc_c_1_13_tail')}
											</>
										}
									/>

									<Clause
										num='1.14'
										body={
											<>
												<strong>{t('termsTc_c_1_14_lead')}</strong>
												{t('termsTc_c_1_14_tail')}
											</>
										}
									/>

									<Clause
										num='1.15'
										body={
											<>
												<strong>{t('termsTc_c_1_15_lead')}</strong>
												{t('termsTc_c_1_15_tail')}
											</>
										}
									/>

									<Clause
										num='1.16'
										body={
											<>
												<strong>{t('termsTc_c_1_16_lead')}</strong>
												{t('termsTc_c_1_16_tail')}
											</>
										}
									/>

									<Clause
										num='1.17'
										body={
											<>
												<strong>{t('termsTc_c_1_17_lead')}</strong>
												{t('termsTc_c_1_17_tail')}
											</>
										}
									/>
								</div>
							</div>

							{/* Article 2 - Scope of Service */}
							<div className={`article-block ${expandedArticles['general-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Scope_of_Service' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='2.1' body={t('termsTc_c_2_1_str')} />
									<Clause num='2.2' body={t('termsTc_c_2_2_str')} />
									<Clause num='2.3' body={t('termsTc_c_2_3_str')} />
									<Clause num='2.4' body={t('termsTc_c_2_4_plain')} />
								</div>
							</div>

							{/* Article 3 - Account Types */}
							<div className={`article-block ${expandedArticles['general-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Account_Types' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='3.1'
										body={
											<>
												<strong>{t('termsTc_c_3_1_lead')}</strong>
												{t('termsTc_c_3_1_tail')}
											</>
										}
									/>
									<Clause
										num='3.2'
										body={
											<>
												<strong>{t('termsTc_c_3_2_lead')}</strong>
												{t('termsTc_c_3_2_tail')}
											</>
										}
									/>
									<Clause
										num='3.3'
										body={
											<>
												<strong>{t('termsTc_c_3_3_lead')}</strong>
												{t('termsTc_c_3_3_tail')}
											</>
										}
									/>
								</div>
							</div>

							{/* Article 4 - Eligibility */}
							<div className={`article-block ${expandedArticles['general-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Eligibility' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='4.1' body={t('termsTc_c_4_1_str')} />

									<Clause num='4.2' body={t('termsTc_c_4_2_str')} />

									<Clause num='4.3' body={t('termsTc_c_4_3_str')} />
								</div>
							</div>

							{/* Article 5 - Account Creation */}
							<div className={`article-block ${expandedArticles['general-5'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-5')}>
									<h3>
										<span className='article-num'>5</span>
										<Text phraseKey='termsTc_txt_Account_Creation' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='5.1' body={t('termsTc_c_5_1_str')} />
									<Clause
										num='5.2'
										body={
											<>
												{t('termsTc_c_5_2_ul_pre')}
												<ul className='sub-list'>
													<li>{t('termsTc_c_5_2_li_1')}</li>
													<li>{t('termsTc_c_5_2_li_2')}</li>
													<li>{t('termsTc_c_5_2_li_3')}</li>
													<li>{t('termsTc_c_5_2_li_4')}</li>
													<li>{t('termsTc_c_5_2_li_5')}</li>
												</ul>
											</>
										}
									/>
									<Clause num='5.3' body={t('termsTc_c_5_3_str')} />
								</div>
							</div>

							{/* Article 6 - Order Initiation Rules */}
							<div className={`article-block ${expandedArticles['general-6'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-6')}>
									<h3>
										<span className='article-num'>6</span>
										<Text phraseKey='termsTc_txt_Order_Initiation_Rules' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='6.1' body={t('termsTc_c_6_1_str')} />

									<Clause num='6.2' body={t('termsTc_c_6_2_str')} />

									<Clause num='6.3' body={t('termsTc_c_6_3_str')} />

									<Clause num='6.4' body={t('termsTc_c_6_4_str')} />
								</div>
							</div>

							{/* Article 7 - Order Stages */}
							<div className={`article-block ${expandedArticles['general-7'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-7')}>
									<h3>
										<span className='article-num'>7</span>
										<Text phraseKey='termsTc_txt_Order_Stages' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='7.1'
										body={
											<>
												{t('termsTc_c_7_1_ul_pre')}
												<ul className='sub-list'>
													<li>{t('termsTc_c_7_1_li_1')}</li>
													<li>{t('termsTc_c_7_1_li_2')}</li>
													<li>{t('termsTc_c_7_1_li_3')}</li>
													<li>{t('termsTc_c_7_1_li_4')}</li>
													<li>{t('termsTc_c_7_1_li_5')}</li>
													<li>{t('termsTc_c_7_1_li_6')}</li>
													<li>{t('termsTc_c_7_1_li_7')}</li>
													<li>{t('termsTc_c_7_1_li_8')}</li>
													<li>{t('termsTc_c_7_1_li_9')}</li>
												</ul>
											</>
										}
									/>
									<Clause num='7.2' body={t('termsTc_c_7_2_str')} />

									<Clause num='7.3' body={t('termsTc_c_7_3_str')} />
								</div>
							</div>

							{/* Article 8 - Escrow Mechanism */}
							<div className={`article-block ${expandedArticles['general-8'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-8')}>
									<h3>
										<span className='article-num'>8</span>
										<Text phraseKey='termsTc_txt_Escrow_Mechanism' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='8.1' body={t('termsTc_c_8_1_str')} />
									<Clause num='8.2' body={t('termsTc_c_8_2_str')} />
									<Clause
										num='8.3'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_8_3_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_8_3_li_1')}</li>
														<li>{t('termsTc_c_8_3_li_2')}</li>
														<li>{t('termsTc_c_8_3_li_3')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_8_3_plain')
											)
										}
									/>
									<Clause num='8.4' body={t('termsTc_c_8_4_str')} />
									<Clause num='8.5' body={t('termsTc_c_8_5_str')} />
								</div>
							</div>

							{/* Article 9 - Payment Methods */}
							<div className={`article-block ${expandedArticles['general-9'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-9')}>
									<h3>
										<span className='article-num'>9</span>
										<Text phraseKey='termsTc_txt_Payment_Methods' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='9.1' body={t('termsTc_c_9_1_str')} />
									<Clause num='9.2' body={t('termsTc_c_9_2_str')} />
									<Clause num='9.3' body={t('termsTc_c_9_3_str')} />
									<Clause num='9.4' body={t('termsTc_c_9_4_str')} />
								</div>
							</div>

							{/* Article 10 - Proof of Transfer */}
							<div className={`article-block ${expandedArticles['general-10'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-10')}>
									<h3>
										<span className='article-num'>10</span>
										<Text phraseKey='termsTc_txt_Proof_of_Transfer' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='10.1' body={t('termsTc_c_10_1_str')} />

									<Clause num='10.2' body={t('termsTc_c_10_2_str')} />

									<Clause num='10.3' body={t('termsTc_c_10_3_str')} />

									<Clause num='10.4' body={t('termsTc_c_10_4_str')} />
								</div>
							</div>

							{/* Article 11 - Official Communication Channels */}
							<div className={`article-block ${expandedArticles['general-11'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-11')}>
									<h3>
										<span className='article-num'>11</span>
										<Text phraseKey='termsTc_txt_Official_Communication_Channels' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='11.1' body={t('termsTc_c_11_1_str')} />

									<Clause num='11.2' body={t('termsTc_c_11_2_str')} />

									<Clause num='11.3' body={t('termsTc_c_11_3_str')} />

									<Clause num='11.4' body={t('termsTc_c_11_4_str')} />
								</div>
							</div>

							{/* Article 12 - Verification */}
							<div className={`article-block ${expandedArticles['general-12'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-12')}>
									<h3>
										<span className='article-num'>12</span>
										<Text phraseKey='termsTc_txt_Verification' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='12.1' body={t('termsTc_c_12_1_str')} />

									<Clause num='12.2' body={t('termsTc_c_12_2_str')} />

									<Clause num='12.3' body={t('termsTc_c_12_3_str')} />
								</div>
							</div>

							{/* Article 13 - Security and Compliance */}
							<div className={`article-block ${expandedArticles['general-13'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-13')}>
									<h3>
										<span className='article-num'>13</span>
										<Text phraseKey='termsTc_txt_Security_and_Compliance' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='13.1' body={t('termsTc_c_13_1_str')} />

									<Clause num='13.2' body={t('termsTc_c_13_2_str')} />

									<Clause num='13.3' body={t('termsTc_c_13_3_str')} />

									<Clause num='13.4' body={t('termsTc_c_13_4_str')} />

									<Clause num='13.5' body={t('termsTc_c_13_5_str')} />
								</div>
							</div>

							{/* Article 14 - Prohibited Activities */}
							<div className={`article-block ${expandedArticles['general-14'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-14')}>
									<h3>
										<span className='article-num'>14</span>
										<Text phraseKey='termsTc_txt_Prohibited_Activities' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='14.1' body={t('termsTc_c_14_1_str')} />

									<Clause num='14.2' body={t('termsTc_c_14_2_str')} />

									<Clause num='14.3' body={t('termsTc_c_14_3_str')} />

									<Clause num='14.4' body={t('termsTc_c_14_4_str')} />

									<Clause num='14.5' body={t('termsTc_c_14_5_str')} />

									<Clause num='14.6' body={t('termsTc_c_14_6_str')} />

									<Clause num='14.7' body={t('termsTc_c_14_7_str')} />
								</div>
							</div>

							{/* Article 15 - Company Authority */}
							<div className={`article-block ${expandedArticles['general-15'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-15')}>
									<h3>
										<span className='article-num'>15</span>
										<Text phraseKey='termsTc_txt_Company_Authority' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='15.1'
										body={
											<>
												{t('termsTc_c_15_1_ul_pre')}
												<ul className='sub-list'>
													<li>{t('termsTc_c_15_1_li_1')}</li>
													<li>{t('termsTc_c_15_1_li_2')}</li>
													<li>{t('termsTc_c_15_1_li_3')}</li>
													<li>{t('termsTc_c_15_1_li_4')}</li>
													<li>{t('termsTc_c_15_1_li_5')}</li>
													<li>{t('termsTc_c_15_1_li_6')}</li>
													<li>{t('termsTc_c_15_1_li_7')}</li>
												</ul>
											</>
										}
									/>
									<Clause num='15.2' body={t('termsTc_c_15_2_str')} />
								</div>
							</div>

							{/* Article 16 - Limitation of Liability */}
							<div className={`article-block ${expandedArticles['general-16'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-16')}>
									<h3>
										<span className='article-num'>16</span>
										<Text phraseKey='termsTc_txt_Limitation_of_Liability' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='16.1'
										body={
											<>
												{t('termsTc_c_16_1_ul_pre')}
												<ul className='sub-list'>
													<li>{t('termsTc_c_16_1_li_1')}</li>
													<li>{t('termsTc_c_16_1_li_2')}</li>
													<li>{t('termsTc_c_16_1_li_3')}</li>
													<li>{t('termsTc_c_16_1_li_4')}</li>
													<li>{t('termsTc_c_16_1_li_5')}</li>
													<li>{t('termsTc_c_16_1_li_6')}</li>
													<li>{t('termsTc_c_16_1_li_7')}</li>
													<li>{t('termsTc_c_16_1_li_8')}</li>
													<li>{t('termsTc_c_16_1_li_9')}</li>
												</ul>
											</>
										}
									/>
									<Clause num='16.2' body={t('termsTc_c_16_2_str')} />
									<Clause num='16.3' body={t('termsTc_c_16_3_str')} />
								</div>
							</div>

							{/* Article 17 - Amendments */}
							<div className={`article-block ${expandedArticles['general-17'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-17')}>
									<h3>
										<span className='article-num'>17</span>
										<Text phraseKey='termsTc_txt_Amendments' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='17.1' body={t('termsTc_c_17_1_str')} />

									<Clause num='17.2' body={t('termsTc_c_17_2_str')} />
								</div>
							</div>
						</div>
					)}

					{/* Order Protection & Balance Escrow Policy Page */}
					{activePage === 'protection' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-shared'>
									<Text phraseKey='termsTc_txt_Shared_Policies_1' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Order_Protection_Balance_Escrow_P_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_The_policy_governing_the_escrow_of' />
								</p>
							</div>

							<div className='intro-block'>
								<Text phraseKey='termsTc_txt_This_Policy_aims_to_regulate_the_or' />
							</div>

							{/* Article 1 - Nature of Protection */}
							<div className={`article-block ${expandedArticles['protection-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_Nature_of_Protection' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='1.1' body={t('termsTc_c_1_1_str')} />
									<Clause num='1.2' body={t('termsTc_c_1_2_str')} />
								</div>
							</div>

							{/* Article 2 - When Escrow Commences */}
							<div className={`article-block ${expandedArticles['protection-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_When_Escrow_Commences' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='2.1' body={t('termsTc_c_2_1_str_1')} />
									<Clause num='2.2' body={t('termsTc_c_2_2_str_1')} />
									<Clause num='2.3' body={t('termsTc_c_2_3_str_1')} />
								</div>
							</div>

							{/* Article 3 - When Escrowed Balance Is Released */}
							<div className={`article-block ${expandedArticles['protection-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_When_Escrowed_Balance_Is_Released' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='3.1' body={t('termsTc_c_3_1_str')} />
									<Clause num='3.2' body={t('termsTc_c_3_2_str')} />
								</div>
							</div>

							{/* Article 4 - Escrow Suspension */}
							<div className={`article-block ${expandedArticles['protection-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Escrow_Suspension' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='4.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_4_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_4_1_li_1')}</li>
														<li>{t('termsTc_c_4_1_li_2')}</li>
														<li>{t('termsTc_c_4_1_li_3')}</li>
														<li>{t('termsTc_c_4_1_li_4')}</li>
														<li>{t('termsTc_c_4_1_li_5')}</li>
														<li>{t('termsTc_c_4_1_li_6')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_4_1_plain')
											)
										}
									/>
								</div>
							</div>

							{/* Article 5 - Return of Balance or Cancellation */}
							<div className={`article-block ${expandedArticles['protection-5'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-5')}>
									<h3>
										<span className='article-num'>5</span>
										<Text phraseKey='termsTc_txt_Return_of_Balance_or_Cancellation_o' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='5.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_5_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_5_1_li_1')}</li>
														<li>{t('termsTc_c_5_1_li_2')}</li>
														<li>{t('termsTc_c_5_1_li_3')}</li>
														<li>{t('termsTc_c_5_1_li_4')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_5_1_plain')
											)
										}
									/>
								</div>
							</div>

							{/* Article 6 - Company Authority */}
							<div className={`article-block ${expandedArticles['protection-6'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-6')}>
									<h3>
										<span className='article-num'>6</span>
										<Text phraseKey='termsTc_txt_Company_Authority_1' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='6.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_6_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_6_1_li_1')}</li>
														<li>{t('termsTc_c_6_1_li_2')}</li>
														<li>{t('termsTc_c_6_1_li_3')}</li>
														<li>{t('termsTc_c_6_1_li_4')}</li>
														<li>{t('termsTc_c_6_1_li_5')}</li>
														<li>{t('termsTc_c_6_1_li_6')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_6_1_plain')
											)
										}
									/>
								</div>
							</div>

							{/* Article 7 - System Abuse */}
							<div className={`article-block ${expandedArticles['protection-7'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('protection-7')}>
									<h3>
										<span className='article-num'>7</span>
										<Text phraseKey='termsTc_txt_System_Abuse' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='7.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_7_1_plain_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_7_1_plain_li_1')}</li>
														<li>{t('termsTc_c_7_1_plain_li_2')}</li>
														<li>{t('termsTc_c_7_1_plain_li_3')}</li>
														<li>{t('termsTc_c_7_1_plain_li_4')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_7_1_plain')
											)
										}
									/>
									<Clause num='7.2' body={t('termsTc_c_7_2_str_1')} />
								</div>
							</div>
						</div>
					)}

					{/* Disputes & Appeals Policy Page */}
					{activePage === 'disputes' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-shared'>
									<Text phraseKey='termsTc_txt_Shared_Policies_2' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Disputes_Appeals_Policy_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_The_policy_governing_the_submission' />
								</p>
							</div>

							<div className='intro-block'>
								<Text phraseKey='termsTc_txt_This_Policy_aims_to_regulate_the_me' />
							</div>

							{/* Article 1 - Grounds for Appeal */}
							<div className={`article-block ${expandedArticles['disputes-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('disputes-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_Grounds_for_Appeal' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause
										num='1.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_1_1_str_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_1_1_str_1_li_1')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_2')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_3')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_4')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_5')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_6')}</li>
														<li>{t('termsTc_c_1_1_str_1_li_7')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_1_1_str_1')
											)
										}
									/>
								</div>
							</div>

							{/* Article 2 - Accepted Evidence */}
							<div className={`article-block ${expandedArticles['general-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Accepted_Evidence' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause
										num='2.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_2_1_str_2_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_2_1_str_2_li_1')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_2')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_3')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_4')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_5')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_6')}</li>
														<li>{t('termsTc_c_2_1_str_2_li_7')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_2_1_str_2')
											)
										}
									/>

									<Clause num='2.2' body={t('termsTc_c_2_2_str_2')} />

									<Clause num='2.3' body={t('termsTc_c_2_3_str_2')} />
								</div>
							</div>

							{/* Article 3 - Company Authority During Disputes */}
							<div className={`article-block ${expandedArticles['general-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Company_Authority_During_Disputes' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='3.1' body={t('termsTc_c_3_1_str_1')} />

									<Clause num='3.2' body={t('termsTc_c_3_2_str_1')} />

									<Clause num='3.3' body={t('termsTc_c_3_3_str')} />
								</div>
							</div>

							{/* Article 4 - Final Decision */}
							<div className={`article-block ${expandedArticles['general-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Final_Decision' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='4.1' body={t('termsTc_c_4_1_str_1')} />

									<Clause num='4.2' body={t('termsTc_c_4_2_str_1')} />
								</div>
							</div>
						</div>
					)}

					{/* Third-Party Policy Page */}
					{activePage === 'thirdparty' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-shared'>
									<Text phraseKey='termsTc_txt_Shared_Policies_3' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Third_Party_Transactions_Policy_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_Controls_applicable_to_transfers_or' />
								</p>
							</div>
							<div className='intro-block'>
								<Text phraseKey='termsTc_txt_This_Policy_regulates_situations_in' />
							</div>

							{/* Article 1 - General Rule */}
							<div className={`article-block ${expandedArticles['thirdparty-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('thirdparty-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_General_Rule' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='1.1' body={t('termsTc_c_1_1_str_2')} />

									<Clause num='1.2' body={t('termsTc_c_1_2_str_1')} />
								</div>
							</div>

							{/* Article 2 - Limits of Company Liability */}
							<div className={`article-block ${expandedArticles['general-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Limits_of_Company_Liability' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause
										num='2.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_2_1_str_3_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_2_1_str_3_li_1')}</li>
														<li>{t('termsTc_c_2_1_str_3_li_2')}</li>
														<li>{t('termsTc_c_2_1_str_3_li_3')}</li>
														<li>{t('termsTc_c_2_1_str_3_li_4')}</li>
														<li>{t('termsTc_c_2_1_str_3_li_5')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_2_1_str_3')
											)
										}
									/>

									<Clause num='2.2' body={t('termsTc_c_2_2_str_3')} />
								</div>
							</div>

							{/* Article 3 - Company's Right of Review */}
							<div className={`article-block ${expandedArticles['general-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Company_s_Right_of_Review' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause
										num='3.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_3_1_str_2_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_3_1_str_2_li_1')}</li>
														<li>{t('termsTc_c_3_1_str_2_li_2')}</li>
														<li>{t('termsTc_c_3_1_str_2_li_3')}</li>
														<li>{t('termsTc_c_3_1_str_2_li_4')}</li>
														<li>{t('termsTc_c_3_1_str_2_li_5')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_3_1_str_2')
											)
										}
									/>
								</div>
							</div>

							{/* Article 4 - Ultimate Responsibility */}
							<div className={`article-block ${expandedArticles['general-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('general-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Ultimate_Responsibility' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='4.1' body={t('termsTc_c_4_1_str_2')} />

									<Clause num='4.2' body={t('termsTc_c_4_2_str_2')} />
								</div>
							</div>
						</div>
					)}

					{/* Client Terms & Conditions Page */}
					{!isMerchant && activePage === 'client-terms' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-client'>
									<Text phraseKey='termsTc_txt_Client_Policies' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Client_Terms_Conditions_P2P_Pla' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_The_terms_and_conditions_governing' />
								</p>
							</div>

							<div className='intro-block'>
								{t('termsTc_intro_clientTerms_p1')}
								<br />
								<br />
								{t('termsTc_intro_clientTerms_p2')}
							</div>

							{/* Article 1 - General Client Obligations */}
							<div className={`article-block ${expandedArticles['client-terms-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_General_Client_Obligations' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='1.1' body={t('termsTc_c_1_1_str_3')} />
									<Clause num='1.2' body={t('termsTc_c_1_2_str_2')} />
									<Clause num='1.3' body={t('termsTc_c_1_3_str')} />
									<Clause num='1.4' body={t('termsTc_c_1_4_str')} />
									<Clause num='1.5' body={t('termsTc_c_1_5_str')} />
								</div>
							</div>

							{/* Article 2 - Deposit Order Obligations */}
							<div className={`article-block ${expandedArticles['client-terms-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Client_Obligations_in_Deposit_Order' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='2.1' body={t('termsTc_c_2_1_str_4')} />
									<Clause num='2.2' body={t('termsTc_c_2_2_str_4')} />
									<Clause num='2.3' body={t('termsTc_c_2_3_str_3')} />
									<Clause num='2.4' body={t('termsTc_c_2_4_str')} />
									<Clause num='2.5' body={t('termsTc_c_2_5_str')} />
								</div>
							</div>

							{/* Article 3 - Withdrawal Order Obligations */}
							<div className={`article-block ${expandedArticles['client-terms-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Client_Obligations_in_Withdrawal_Or' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='3.1' body={t('termsTc_c_3_1_str_3')} />
									<Clause num='3.2' body={t('termsTc_c_3_2_str_2')} />
									<Clause num='3.3' body={t('termsTc_c_3_3_str_1')} />
									<Clause num='3.4' body={t('termsTc_c_3_4_str')} />
								</div>
							</div>

							{/* Article 4 - Wallet & Internal Transfers */}
							<div className={`article-block ${expandedArticles['client-terms-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Wallet_Internal_Transfers' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='4.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_4_1_plain_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_4_1_plain_1_li_1')}</li>
														<li>{t('termsTc_c_4_1_plain_1_li_2')}</li>
														<li>{t('termsTc_c_4_1_plain_1_li_3')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_4_1_plain_1')
											)
										}
									/>
									<Clause num='4.2' body={t('termsTc_c_4_2_str_3')} />
								</div>
							</div>

							{/* Article 5 - Disputes & Objections */}
							<div className={`article-block ${expandedArticles['client-terms-5'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-5')}>
									<h3>
										<span className='article-num'>5</span>
										<Text phraseKey='termsTc_txt_Disputes_Objections' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='5.1' body={t('termsTc_c_5_1_str_1')} />
									<Clause num='5.2' body={t('termsTc_c_5_2_str')} />
									<Clause num='5.3' body={t('termsTc_c_5_3_str_1')} />
									<Clause num='5.4' body={t('termsTc_c_5_4_str')} />
								</div>
							</div>

							{/* Article 6 - Client Liability */}
							<div className={`article-block ${expandedArticles['client-terms-6'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-terms-6')}>
									<h3>
										<span className='article-num'>6</span>
										<Text phraseKey='termsTc_txt_Client_Liability' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='6.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_6_1_plain_1_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_6_1_plain_1_li_1')}</li>
														<li>{t('termsTc_c_6_1_plain_1_li_2')}</li>
														<li>{t('termsTc_c_6_1_plain_1_li_3')}</li>
														<li>{t('termsTc_c_6_1_plain_1_li_4')}</li>
														<li>{t('termsTc_c_6_1_plain_1_li_5')}</li>
														<li>{t('termsTc_c_6_1_plain_1_li_6')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_6_1_plain_1')
											)
										}
									/>
									<Clause num='6.2' body={t('termsTc_c_6_2_str_1')} />
								</div>
							</div>
						</div>
					)}

					{/* Deposit & Withdrawal Policy Page */}
					{!isMerchant && activePage === 'client-ops' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-client'>
									<Text phraseKey='termsTc_txt_Client_Policies_1' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Deposit_Withdrawal_Policy_P2P_P' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_Controls_and_procedures_governing_d' />
								</p>
							</div>

							{/* Article 1 - Deposit Policy */}
							<div className={`article-block ${expandedArticles['client-ops-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('client-ops-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_Deposit_Policy' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div className='steps-block'>
										<div className='step-item'>
											<span className='step-num'>1</span>
											<Text phraseKey='termsTc_txt_The_Client_begins_by_selecting_the' />
										</div>
										<div className='step-item'>
											<span className='step-num'>2</span>
											<Text phraseKey='termsTc_txt_The_Client_specifies_the_amount_and' />
										</div>
										<div className='step-item'>
											<span className='step-num'>3</span>
											<Text phraseKey='termsTc_txt_After_the_Merchant_accepts_the_orde' />
										</div>
										<div className='step-item'>
											<span className='step-num'>4</span>
											<Text phraseKey='termsTc_txt_The_Merchant_sends_their_payment_de' />
										</div>
										<div className='step-item'>
											<span className='step-num'>5</span>
											<Text phraseKey='termsTc_txt_The_Client_executes_the_actual_tran' />
										</div>
										<div className='step-item'>
											<span className='step-num'>6</span>
											<Text phraseKey='termsTc_txt_After_the_Merchant_verifies_actual' />
										</div>
										<div className='step-item'>
											<span className='step-num'>7</span>
											<Text phraseKey='termsTc_txt_The_escrowed_balance_is_transferred' />
										</div>
										<div className='step-item'>
											<span className='step-num'>8</span>
											<Text phraseKey='termsTc_txt_The_Client_may_then_transfer_funds' />
										</div>
									</div>
								</div>
							</div>

							{/* Article 2 - Withdrawal Policy */}
							<div className={`article-block ${expandedArticles['ops-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('ops-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Withdrawal_Policy' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div className='steps-block'>
										<div className='step-item'>
											<span className='step-num'>1</span>
											<Text phraseKey='termsTc_txt_The_Client_begins_by_transferring_t' />
										</div>
										<div className='step-item'>
											<span className='step-num'>2</span>
											<Text phraseKey='termsTc_txt_The_Client_selects_the_appropriate' />
										</div>
										<div className='step-item'>
											<span className='step-num'>3</span>
											<Text phraseKey='termsTc_txt_The_Client_specifies_the_amount_and_1' />
										</div>
										<div className='step-item'>
											<span className='step-num'>4</span>
											<Text phraseKey='termsTc_txt_After_the_Merchant_accepts_the_orde_1' />
										</div>
										<div className='step-item'>
											<span className='step-num'>5</span>
											<Text phraseKey='termsTc_txt_The_Client_sends_their_receipt_deta' />
										</div>
										<div className='step-item'>
											<span className='step-num'>6</span>
											<Text phraseKey='termsTc_txt_The_Merchant_transfers_funds_to_the' />
										</div>
										<div className='step-item'>
											<span className='step-num'>7</span>
											<Text phraseKey='termsTc_txt_The_Merchant_uploads_valid_proof_of' />
										</div>
										<div className='step-item'>
											<span className='step-num'>8</span>
											<Text phraseKey='termsTc_txt_After_the_Client_confirms_actual_re' />
										</div>
									</div>
								</div>
							</div>

							{/* Article 3 - Shared Controls */}
							<div className={`article-block ${expandedArticles['ops-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('ops-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Shared_Controls' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='3.1' body={t('termsTc_c_3_1_str_4')} />
									<Clause num='3.2' body={t('termsTc_c_3_2_str_3')} />
									<Clause num='3.3' body={t('termsTc_c_3_3_str_2')} />
									<Clause num='3.4' body={t('termsTc_c_3_4_str_1')} />
									<Clause num='3.5' body={t('termsTc_c_3_5_str')} />
								</div>
							</div>
						</div>
					)}

					{/* Client Violations & Penalties System Page */}
					{!isMerchant && activePage === 'client-violations' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-client'>
									<Text phraseKey='termsTc_txt_Client_Policies_2' />
								</span>
								<h2>
									<Text phraseKey='termsTc_txt_Client_Violations_Penalties_Syste' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_txt_Classification_of_behavioral_and_op' />
								</p>
							</div>

							<div className='intro-block'>
								{t('termsTc_intro_violations_p1')}
								<br />
								<br />
								{t('termsTc_intro_violations_p2')}
							</div>

							{/* Article 1 - Client Violations */}
							<div className={`article-block ${expandedArticles['violations-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_txt_Client_Violations' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
											<Text phraseKey='termsTc_txt_1_1_Order_Creation_Violations' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Opening_an_order_without_genuine_in' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Opening_an_order_and_then_abandonin' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Repeatedly_opening_orders_in_a_friv' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Opening_an_order_and_immediately_ca' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Attempting_to_open_more_than_one_or' />
											</li>
										</ul>
									</div>

									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
											<Text phraseKey='termsTc_txt_1_2_Deposit_Order_Violations' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Pressing_the_payment_confirmation_o' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Uploading_an_inaccurate_unclear_m' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Claiming_completion_of_a_transfer_w' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Unjustified_delay_in_uploading_proo' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Refusing_to_cooperate_with_verifica' />
											</li>
										</ul>
									</div>

									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
											<Text phraseKey='termsTc_txt_1_3_Withdrawal_Order_Violations' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Confirming_receipt_of_funds_or_rele' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Refusing_to_release_the_order_after' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Claiming_non_receipt_of_funds_despi' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Providing_incorrect_incomplete_or' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Obstructing_completion_of_the_order' />
											</li>
										</ul>
									</div>

									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
											<Text phraseKey='termsTc_txt_1_4_Communication_Conduct_Violati' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Use_of_abusive_language_threats_o' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Attempting_to_coerce_extort_or_th' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Attempting_to_move_the_transaction' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Exchanging_personal_contact_details' />
											</li>
										</ul>
									</div>

									<div>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem' }}>
											<Text phraseKey='termsTc_txt_1_5_Security_Verification_Violati' />
										</h4>
										<ul className='sub-list'>
											<li>
												<Text phraseKey='termsTc_txt_Providing_inaccurate_misleading_o' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Refusing_or_obstructing_verificatio' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Using_a_third_party_in_a_manner_tha' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Creating_or_using_multiple_or_linke' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Misusing_the_dispute_mechanism_or_f' />
											</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Article 2 - Measures for Repeated Violations */}
							<div className={`article-block ${expandedArticles['violations-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_txt_Measures_Penalties_for_Repeated_V' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='2.1' body={t('termsTc_c_2_1_str_5')} />
									<Clause num='2.2' body={t('termsTc_c_2_2_str_5')} />
									<Clause num='2.3' body={t('termsTc_c_2_3_str_4')} />
									<Clause num='2.4' body={t('termsTc_c_2_4_str_1')} />
								</div>
							</div>

							{/* Article 3 - Immediate Permanent Ban Cases */}
							<div className={`article-block ${expandedArticles['violations-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_txt_Immediate_Permanent_Ban_Cases' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='3.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_3_1_plain_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_3_1_plain_li_1')}</li>
														<li>{t('termsTc_c_3_1_plain_li_2')}</li>
														<li>{t('termsTc_c_3_1_plain_li_3')}</li>
														<li>{t('termsTc_c_3_1_plain_li_4')}</li>
														<li>{t('termsTc_c_3_1_plain_li_5')}</li>
														<li>{t('termsTc_c_3_1_plain_li_6')}</li>
														<li>{t('termsTc_c_3_1_plain_li_7')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_3_1_plain')
											)
										}
									/>
								</div>
							</div>

							{/* Article 4 - Violation Classification */}
							<div className={`article-block ${expandedArticles['violations-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_txt_Violation_Classification' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='4.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_c_4_1_plain_2_ul_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_c_4_1_plain_2_li_1')}</li>
														<li>{t('termsTc_c_4_1_plain_2_li_2')}</li>
														<li>{t('termsTc_c_4_1_plain_2_li_3')}</li>
													</ul>
												</>
											) : (
												t('termsTc_c_4_1_plain_2')
											)
										}
									/>
									<Clause num='4.2' body={t('termsTc_c_4_2_str_4')} />
								</div>
							</div>

							{/* Article 5 - Measures Available */}
							<div className={`article-block ${expandedArticles['violations-5'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-5')}>
									<h3>
										<span className='article-num'>5</span>
										<Text phraseKey='termsTc_txt_Measures_Available_Against_the_Clie' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem', color: '#f0c060' }}>
											<Text phraseKey='termsTc_txt_A_Corrective_and_Warning_Measures' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Issuing_a_warning_or_formal_notice' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Requiring_the_Client_to_complete_ve' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Temporarily_restricting_certain_acc' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Restricting_the_ability_to_open_new' />
											</li>
										</ul>
									</div>

									<div style={{ marginBottom: '1.5rem' }}>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem', color: '#f0c060' }}>
											<Text phraseKey='termsTc_txt_B_Restrictive_Measures' />
										</h4>
										<ul className='sub-list' style={{ marginBottom: '1rem' }}>
											<li>
												<Text phraseKey='termsTc_txt_Temporarily_suspending_the_ability' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Temporarily_suspending_the_Client_s' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Temporarily_suspending_internal_tra' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Placing_the_account_under_review_or' />
											</li>
										</ul>
									</div>

									<div>
										<h4 style={{ fontSize: '0.9rem', fontWeight: 700, marginBottom: '0.5rem', color: '#ff8080' }}>
											<Text phraseKey='termsTc_txt_C_Final_or_Escalated_Measures' />
										</h4>
										<ul className='sub-list'>
											<li>
												<Text phraseKey='termsTc_txt_Rejecting_the_objection_or_order_if' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Closing_the_order_or_taking_the_app' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Permanently_suspending_the_Client_s' />
											</li>
											<li>
												<Text phraseKey='termsTc_txt_Banning_any_linked_accounts_if_they' />
											</li>
										</ul>
									</div>
								</div>
							</div>

							{/* Article 6 - Criteria for Measures */}
							<div className={`article-block ${expandedArticles['violations-6'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-6')}>
									<h3>
										<span className='article-num'>6</span>
										<Text phraseKey='termsTc_txt_Criteria_for_Assessing_the_Appropri' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<p style={{ fontSize: '0.87rem', color: '#666', marginBottom: '0.75rem' }}>
										<Text phraseKey='termsTc_txt_The_Company_may_take_the_following' />
									</p>
									<ul className='sub-list'>
										<li>
											<Text phraseKey='termsTc_txt_The_nature_and_seriousness_of_the_v' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_number_of_times_the_violation_w' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_effect_of_the_violation_on_the' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_Client_s_degree_of_cooperation' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_Client_s_prior_record_on_the_Pl' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_existence_of_indications_of_fra' />
										</li>
										<li>
											<Text phraseKey='termsTc_txt_The_number_of_orders_or_accounts_af' />
										</li>
									</ul>
								</div>
							</div>

							{/* Article 7 - Protection of Merchant Rights */}
							<div className={`article-block ${expandedArticles['violations-7'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-7')}>
									<h3>
										<span className='article-num'>7</span>
										<Text phraseKey='termsTc_txt_Protection_of_Merchant_Rights_and_t' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='7.1' body={t('termsTc_c_7_1_str')} />
									<Clause num='7.2' body={t('termsTc_c_7_2_str_2')} />
									<Clause num='7.3' body={t('termsTc_c_7_3_str_1')} />
								</div>
							</div>

							{/* Article 8 - Abuse of Appeals */}
							<div className={`article-block ${expandedArticles['violations-8'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-8')}>
									<h3>
										<span className='article-num'>8</span>
										<Text phraseKey='termsTc_txt_Abuse_of_Appeals' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='8.1' body={t('termsTc_c_8_1_str_1')} />
									<Clause num='8.2' body={t('termsTc_c_8_2_str_1')} />
								</div>
							</div>

							{/* Article 9 - Multiple or Linked Accounts */}
							<div className={`article-block ${expandedArticles['violations-9'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-9')}>
									<h3>
										<span className='article-num'>9</span>
										<Text phraseKey='termsTc_txt_Multiple_or_Linked_Accounts' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='9.1' body={t('termsTc_c_9_1_str_1')} />
									<Clause num='9.2' body={t('termsTc_c_9_2_str_1')} />
								</div>
							</div>

							{/* Article 10 - Final Decision */}
							<div className={`article-block ${expandedArticles['violations-10'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('violations-10')}>
									<h3>
										<span className='article-num'>10</span>
										<Text phraseKey='termsTc_txt_Final_Decision_1' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='10.1' body={t('termsTc_c_10_1_str_1')} />
									<Clause num='10.2' body={t('termsTc_c_10_2_str_1')} />
								</div>
							</div>
						</div>
					)}

					{/* MERCHANT_PAGES_START */}
					{isMerchant && activePage === 'merchant-terms' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-merchant'>
									<Text phraseKey='termsTc_mt_mterms_txt_0' />
								</span>
								<h2>
									<Text phraseKey='termsTc_mt_mterms_txt_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_mt_mterms_txt_2' />
								</p>
								<div className='intro-block mt-8'>
									<p>{t('termsTc_mt_mterms_intro_p1')}</p>
									<p>{t('termsTc_mt_mterms_intro_p2')}</p>
								</div>

								{/* Article 1 - Merchant Account Approval */}
								<div className={`article-block ${expandedArticles['merchant-terms-1'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-1')}>
										<h3>
											<span className='article-num'>1</span>
											<Text phraseKey='termsTc_mt_mterms_txt_3' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='1.1' body={t('termsTc_mt_mterms_c_1_1_body')} />
										<Clause num='1.2' body={t('termsTc_mt_mterms_c_1_2_body')} />
										<Clause num='1.3' body={t('termsTc_mt_mterms_c_1_3_body')} />
									</div>
								</div>

								{/* Article 2 - Nature of Merchant Account */}
								<div className={`article-block ${expandedArticles['merchant-terms-2'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-2')}>
										<h3>
											<span className='article-num'>2</span>
											<Text phraseKey='termsTc_mt_mterms_txt_4' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='2.1' body={t('termsTc_mt_mterms_c_2_1_body')} />
										<Clause num='2.2' body={t('termsTc_mt_mterms_c_2_2_body')} />
										<Clause num='2.3' body={t('termsTc_mt_mterms_c_2_3_body')} />
									</div>
								</div>

								{/* Article 3 - Offer Management */}
								<div className={`article-block ${expandedArticles['merchant-terms-3'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-3')}>
										<h3>
											<span className='article-num'>3</span>
											<Text phraseKey='termsTc_mt_mterms_txt_5' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='3.1' body={t('termsTc_mt_mterms_c_3_1_body')} />
										<Clause
											num='3.2'
											body={
												termsUiLang === 'ar' ? (
													<>
														{t('termsTc_mt_mterms_c_3_2_ar_pre')}
														<ul className='sub-list'>
															<li>{t('termsTc_mt_mterms_c_3_2_ar_li_1')}</li>
															<li>{t('termsTc_mt_mterms_c_3_2_ar_li_2')}</li>
															<li>{t('termsTc_mt_mterms_c_3_2_ar_li_3')}</li>
															<li>{t('termsTc_mt_mterms_c_3_2_ar_li_4')}</li>
															<li>{t('termsTc_mt_mterms_c_3_2_ar_li_5')}</li>
														</ul>
													</>
												) : (
													t('termsTc_mt_mterms_c_3_2_en')
												)
											}
										/>
										<Clause num='3.3' body={t('termsTc_mt_mterms_c_3_3_body')} />
										<Clause num='3.4' body={t('termsTc_mt_mterms_c_3_4_body')} />
									</div>
								</div>

								{/* Article 4 - Profit Margin & Ranking */}
								<div className={`article-block ${expandedArticles['merchant-terms-4'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-4')}>
										<h3>
											<span className='article-num'>4</span>
											<Text phraseKey='termsTc_mt_mterms_txt_6' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='4.1' body={t('termsTc_mt_mterms_c_4_1_body')} />
										<Clause num='4.2' body={t('termsTc_mt_mterms_c_4_2_body')} />
										<Clause num='4.3' body={t('termsTc_mt_mterms_c_4_3_body')} />
										<Clause
											num='4.4'
											body={
												termsUiLang === 'ar' ? (
													<>
														{t('termsTc_mt_mterms_c_4_4_ar_pre')}
														<ul className='sub-list'>
															<li>{t('termsTc_mt_mterms_c_4_4_ar_li_1')}</li>
															<li>{t('termsTc_mt_mterms_c_4_4_ar_li_2')}</li>
															<li>{t('termsTc_mt_mterms_c_4_4_ar_li_3')}</li>
														</ul>
													</>
												) : (
													t('termsTc_mt_mterms_c_4_4_en')
												)
											}
										/>
										<Clause num='4.5' body={t('termsTc_mt_mterms_c_4_5_body')} />
									</div>
								</div>

								{/* Article 5 - General Merchant Obligations */}
								<div className={`article-block ${expandedArticles['merchant-terms-5'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-5')}>
										<h3>
											<span className='article-num'>5</span>
											<Text phraseKey='termsTc_mt_mterms_txt_7' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='5.1' body={t('termsTc_mt_mterms_c_5_1_body')} />
										<Clause num='5.2' body={t('termsTc_mt_mterms_c_5_2_body')} />
										<Clause num='5.3' body={t('termsTc_mt_mterms_c_5_3_body')} />
										<Clause num='5.4' body={t('termsTc_mt_mterms_c_5_4_body')} />
										<Clause num='5.5' body={t('termsTc_mt_mterms_c_5_5_body')} />
									</div>
								</div>

								{/* Article 6 - Deposit Order Obligations */}
								<div className={`article-block ${expandedArticles['merchant-terms-6'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-6')}>
										<h3>
											<span className='article-num'>6</span>
											<Text phraseKey='termsTc_mt_mterms_txt_8' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause
											num='6.1'
											body={
												<>
													{t('termsTc_mt_mterms_c_6_1_intro')}
													<ul className='sub-list'>
														<li>{t('termsTc_mt_mterms_c_6_1_li_1')}</li>
														<li>{t('termsTc_mt_mterms_c_6_1_li_2')}</li>
														<li>{t('termsTc_mt_mterms_c_6_1_li_3')}</li>
														<li>{t('termsTc_mt_mterms_c_6_1_li_4')}</li>
														<li>{t('termsTc_mt_mterms_c_6_1_li_5')}</li>
													</ul>
												</>
											}
										/>
										<Clause num='6.2' body={t('termsTc_mt_mterms_c_6_2_body')} />
										<Clause num='6.3' body={t('termsTc_mt_mterms_c_6_3_body')} />
										<Clause num='6.4' body={t('termsTc_mt_mterms_c_6_4_body')} />
										<Clause num='6.5' body={t('termsTc_mt_mterms_c_6_5_body')} />
									</div>
								</div>

								{/* Article 7 - Withdrawal Order Obligations */}
								<div className={`article-block ${expandedArticles['merchant-terms-7'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-7')}>
										<h3>
											<span className='article-num'>7</span>
											<Text phraseKey='termsTc_mt_mterms_txt_9' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause
											num='7.1'
											body={
												<>
													{t('termsTc_mt_mterms_c_7_1_intro')}
													<ul className='sub-list'>
														<li>{t('termsTc_mt_mterms_c_7_1_li_1')}</li>
														<li>{t('termsTc_mt_mterms_c_7_1_li_2')}</li>
														<li>{t('termsTc_mt_mterms_c_7_1_li_3')}</li>
														<li>{t('termsTc_mt_mterms_c_7_1_li_4')}</li>
													</ul>
												</>
											}
										/>
										<Clause num='7.2' body={t('termsTc_mt_mterms_c_7_2_body')} />
										<Clause num='7.3' body={t('termsTc_mt_mterms_c_7_3_body')} />
										<Clause num='7.4' body={t('termsTc_mt_mterms_c_7_4_body')} />
									</div>
								</div>

								{/* Article 8 - Financial Verification & Security */}
								<div className={`article-block ${expandedArticles['merchant-terms-8'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-8')}>
										<h3>
											<span className='article-num'>8</span>
											<Text phraseKey='termsTc_mt_mterms_txt_10' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='8.1' body={t('termsTc_mt_mterms_c_8_1_body')} />
										<Clause num='8.2' body={t('termsTc_mt_mterms_c_8_2_body')} />
										<Clause num='8.3' body={t('termsTc_mt_mterms_c_8_3_body')} />
									</div>
								</div>

								{/* Article 9 - Monitoring & Evaluation */}
								<div className={`article-block ${expandedArticles['merchant-terms-9'] ? 'open' : ''}`}>
									<div className='article-header' onClick={() => toggleArticle('merchant-terms-9')}>
										<h3>
											<span className='article-num'>9</span>
											<Text phraseKey='termsTc_mt_mterms_txt_11' />
										</h3>
										<span className='article-toggle'>▼</span>
									</div>
									<div className='article-body'>
										<Clause num='9.1' body={t('termsTc_mt_mterms_c_9_1_body')} />
										<Clause num='9.2' body={t('termsTc_mt_mterms_c_9_2_body')} />
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Merchant Operations Policy Page */}
					{isMerchant && activePage === 'merchant-ops' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-merchant'>
									<Text phraseKey='termsTc_mt_mops_txt_0' />
								</span>
								<h2>
									<Text phraseKey='termsTc_mt_mops_txt_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_mt_mops_txt_2' />
								</p>
							</div>

							{/* Article 1 - General Policy */}
							<div className={`article-block ${expandedArticles['merchant-ops-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-ops-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_mt_mops_txt_3' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='1.1' body={t('termsTc_mt_mops_c_1_1_body')} />
									<Clause num='1.2' body={t('termsTc_mt_mops_c_1_2_body')} />
									<Clause num='1.3' body={t('termsTc_mt_mops_c_1_3_body')} />
									<Clause num='1.4' body={t('termsTc_mt_mops_c_1_4_body')} />
									<Clause num='1.5' body={t('termsTc_mt_mops_c_1_5_body')} />
								</div>
							</div>

							{/* Article 2 - Processing Deposit Orders */}
							<div className={`article-block ${expandedArticles['merchant-ops-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-ops-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_mt_mops_txt_4' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div className='steps-block'>
										<div className='step-item'>
											<span className='step-num'>1</span>
											<Text phraseKey='termsTc_mt_mops_txt_5' />
										</div>
										<div className='step-item'>
											<span className='step-num'>2</span>
											<Text phraseKey='termsTc_mt_mops_txt_6' />
										</div>
										<div className='step-item'>
											<span className='step-num'>3</span>
											<Text phraseKey='termsTc_mt_mops_txt_7' />
										</div>
										<div className='step-item'>
											<span className='step-num'>4</span>
											<Text phraseKey='termsTc_mt_mops_txt_8' />
										</div>
										<div className='step-item'>
											<span className='step-num'>5</span>
											<Text phraseKey='termsTc_mt_mops_txt_9' />
										</div>
										<div className='step-item'>
											<span className='step-num'>6</span>
											<Text phraseKey='termsTc_mt_mops_txt_10' />
										</div>
									</div>
								</div>
							</div>

							{/* Article 3 - Processing Withdrawal Orders */}
							<div className={`article-block ${expandedArticles['merchant-ops-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-ops-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_mt_mops_txt_11' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<div className='steps-block'>
										<div className='step-item'>
											<span className='step-num'>1</span>
											<Text phraseKey='termsTc_mt_mops_txt_12' />
										</div>
										<div className='step-item'>
											<span className='step-num'>2</span>
											<Text phraseKey='termsTc_mt_mops_txt_13' />
										</div>
										<div className='step-item'>
											<span className='step-num'>3</span>
											<Text phraseKey='termsTc_mt_mops_txt_14' />
										</div>
										<div className='step-item'>
											<span className='step-num'>4</span>
											<Text phraseKey='termsTc_mt_mops_txt_15' />
										</div>
										<div className='step-item'>
											<span className='step-num'>5</span>
											<Text phraseKey='termsTc_mt_mops_txt_16' />
										</div>
									</div>
								</div>
							</div>

							{/* Article 4 - Specific Rules */}
							<div className={`article-block ${expandedArticles['merchant-ops-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-ops-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_mt_mops_txt_17' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='4.1' body={t('termsTc_mt_mops_c_4_1_body')} />
									<Clause num='4.2' body={t('termsTc_mt_mops_c_4_2_body')} />
									<Clause num='4.3' body={t('termsTc_mt_mops_c_4_3_body')} />
									<Clause num='4.4' body={t('termsTc_mt_mops_c_4_4_body')} />
									<Clause num='4.5' body={t('termsTc_mt_mops_c_4_5_body')} />
								</div>
							</div>
						</div>
					)}

					{/* Merchant Violations & Penalties Page */}
					{isMerchant && activePage === 'merchant-violations' && (
						<div className='policy-section active'>
							<div className='section-header'>
								<span className='badge cat-merchant'>
									<Text phraseKey='termsTc_mt_mviol_txt_0' />
								</span>
								<h2>
									<Text phraseKey='termsTc_mt_mviol_txt_1' />
								</h2>
								<p className='section-description'>
									<Text phraseKey='termsTc_mt_mviol_txt_2' />
								</p>
							</div>

							<div className='intro-block'>
								<Text phraseKey='termsTc_mt_mviol_txt_3' />
							</div>

							{/* Article 1 - Operational Violations */}
							<div className={`article-block ${expandedArticles['merchant-violations-1'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-1')}>
									<h3>
										<span className='article-num'>1</span>
										<Text phraseKey='termsTc_mt_mviol_txt_4' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<p style={{ fontSize: '0.87rem', color: '#999', marginBottom: '0.75rem' }}>
										<Text phraseKey='termsTc_mt_mviol_txt_5' />
									</p>
									<Clause num='1.1' body={t('termsTc_mt_mviol_c_1_1_body')} />
									<Clause num='1.2' body={t('termsTc_mt_mviol_c_1_2_body')} />
									<Clause num='1.3' body={t('termsTc_mt_mviol_c_1_3_body')} />
									<Clause num='1.4' body={t('termsTc_mt_mviol_c_1_4_body')} />
									<Clause num='1.5' body={t('termsTc_mt_mviol_c_1_5_body')} />
									<Clause num='1.6' body={t('termsTc_mt_mviol_c_1_6_body')} />
									<Clause num='1.7' body={t('termsTc_mt_mviol_c_1_7_body')} />
									<Clause num='1.8' body={t('termsTc_mt_mviol_c_1_8_body')} />
									<Clause num='1.9' body={t('termsTc_mt_mviol_c_1_9_body')} />
									<Clause num='1.10' body={t('termsTc_mt_mviol_c_1_10_body')} />
									<Clause num='1.11' body={t('termsTc_mt_mviol_c_1_11_body')} />
									<Clause num='1.12' body={t('termsTc_mt_mviol_c_1_12_body')} />
								</div>
							</div>

							{/* Article 2 - Violation Documentation */}
							<div className={`article-block ${expandedArticles['merchant-violations-2'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-2')}>
									<h3>
										<span className='article-num'>2</span>
										<Text phraseKey='termsTc_mt_mviol_txt_6' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='2.1' body={t('termsTc_mt_mviol_c_2_1_body')} />
									<Clause num='2.2' body={t('termsTc_mt_mviol_c_2_2_body')} />
								</div>
							</div>

							{/* Article 3 - Progressive Penalties */}
							<div className={`article-block ${expandedArticles['merchant-violations-3'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-3')}>
									<h3>
										<span className='article-num'>3</span>
										<Text phraseKey='termsTc_mt_mviol_txt_7' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause
										num='3.1'
										body={
											termsUiLang === 'ar' ? (
												<>
													{t('termsTc_mt_mviol_c_3_1_ar_pre')}
													<ul className='sub-list'>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_1')}</li>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_2')}</li>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_3')}</li>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_4')}</li>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_5')}</li>
														<li>{t('termsTc_mt_mviol_c_3_1_ar_li_6')}</li>
													</ul>
												</>
											) : (
												t('termsTc_mt_mviol_c_3_1_en')
											)
										}
									/>
								</div>
							</div>

							{/* Article 4 - Penalty Enforcement */}
							<div className={`article-block ${expandedArticles['merchant-violations-4'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-4')}>
									<h3>
										<span className='article-num'>4</span>
										<Text phraseKey='termsTc_mt_mviol_txt_8' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='4.1' body={t('termsTc_mt_mviol_c_4_1_body')} />
									<Clause num='4.2' body={t('termsTc_mt_mviol_c_4_2_body')} />
									<Clause num='4.3' body={t('termsTc_mt_mviol_c_4_3_body')} />
								</div>
							</div>

							{/* Article 5 - Penalty Assessment Criteria */}
							<div className={`article-block ${expandedArticles['merchant-violations-5'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-5')}>
									<h3>
										<span className='article-num'>5</span>
										<Text phraseKey='termsTc_mt_mviol_txt_9' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>
								<div className='article-body'>
									<Clause num='5.1' body={t('termsTc_mt_mviol_c_5_1_body')} />
									<Clause num='5.2' body={t('termsTc_mt_mviol_c_5_2_body')} />
									<Clause num='5.3' body={t('termsTc_mt_mviol_c_5_3_body')} />
									<Clause num='5.4' body={t('termsTc_mt_mviol_c_5_4_body')} />
									<Clause num='5.5' body={t('termsTc_mt_mviol_c_5_5_body')} />
									<Clause num='5.6' body={t('termsTc_mt_mviol_c_5_6_body')} />
								</div>
							</div>

							{/* Article 6 - Protection of Client Rights */}
							<div className={`article-block ${expandedArticles['merchant-violations-6'] ? 'open' : ''}`}>
								<div className='article-header' onClick={() => toggleArticle('merchant-violations-6')}>
									<h3>
										<span className='article-num'>6</span>
										<Text phraseKey='termsTc_mt_mviol_txt_10' />
									</h3>
									<span className='article-toggle'>▼</span>
								</div>

								<div className='article-body'>
									<Clause num='6.1' body={t('termsTc_mt_mviol_c_6_1_body')} />

									<Clause num='6.2' body={t('termsTc_mt_mviol_c_6_2_body')} />

									<Clause num='6.3' body={t('termsTc_mt_mviol_c_6_3_body')} />
								</div>
							</div>
						</div>
					)}

					{/* MERCHANT_PAGES_END */}
				</main>
			</div>

			{/* Back to Top Button */}
			<button
				className={`back-to-top lang-${termsUiLang} ${showBackToTop ? 'visible' : ''}`}
				onClick={scrollToTop}
				aria-label={t('termsTc_aria_backToTop') as string}
			>
				↑
			</button>
		</div>
	);
}

// Helper Component for Clauses
function Clause({ num, body }: { num: string; body: ReactNode }) {
	return (
		<div className='clause'>
			<span className='clause-num'>{num}</span>
			<div className='clause-text'>{body}</div>
		</div>
	);
}
